import { FormControlLabel, AccordionSummary, Accordion, AccordionDetails, Autocomplete, Grid, TextField, Typography, Divider } from '@mui/material';
import React from 'react';
import { WO2Checkbox, WO2ToggleSwitch } from 'src/common';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { BaseReportComponentProps, HidingOptionCurrencyExposure, HidingOptionsRequest, MdaReportTypeField, MdaReportTypes } from '../../store/types';
import { ExpandMore } from '@mui/icons-material';
import { DateRange } from 'src/store';
import { ClientPortfolio } from '../../../common/store';
import { ReportGroupingOption } from '../ReportGroupingOption';

export interface CurrencyExposureProps extends BaseReportComponentProps {
  setCurrencyExposureSelectedParameter: ActionCreatorWithPayload<boolean, string>;
  expandCurrencyExposure: ActionCreatorWithoutPayload<string>;
  setAsAtDateCurrencyExposureParameter: ActionCreatorWithPayload<DateRange>;
  setCurrencyExposurePortfolioParameter?: ActionCreatorWithPayload<number>;
  setCurrencyExposureHidingOptions: ActionCreatorWithPayload<HidingOptionsRequest<HidingOptionCurrencyExposure>, string>;
  clientPortfolios: ClientPortfolio[];
}

export const CurrencyExposure = ({
  parameters,
  setCurrencyExposureSelectedParameter,
  setAsAtDateCurrencyExposureParameter,
  setCurrencyExposureHidingOptions,
  setCurrencyExposurePortfolioParameter,
  setGroupConsolidatedOption,
  expandCurrencyExposure,
  createReportSortRequest,
  clientPortfolios,
}: CurrencyExposureProps): JSX.Element => {
  const handleClickSelected = () => {
    setCurrencyExposureSelectedParameter(!parameters.selectedReports?.currencyExposure.selected);
    createReportSortRequest(
      !parameters.selectedReports?.currencyExposure.selected,
      MdaReportTypes.CurrencyExposure.id,
      MdaReportTypes.CurrencyExposure.displayName
    );
  };

  const handleClickDateForCurrencyExposure = (dates: DateRange) => {
    setAsAtDateCurrencyExposureParameter(dates);
  };

  const handleSelectPortfolio = (portfolio: ClientPortfolio | null) => {
    if (!setCurrencyExposurePortfolioParameter) return;
    setCurrencyExposurePortfolioParameter(portfolio?.investmentProgramId ?? -1);
  };

  return (
    <Accordion onChange={expandCurrencyExposure} expanded={parameters.selectedReports?.currencyExposure.expanded || false} elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          sx={{ flex: 1 }}
          aria-label="Acknowledge"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox color="primary" checked={parameters.selectedReports?.currencyExposure.selected} onChangeHandler={handleClickSelected} />
          }
          label="Currency Exposure"
        />
        <div onClick={(e) => e.stopPropagation()}>
          <DateRangePicker
            sx={{ minWidth: 194 }}
            dateRange={{
              dateFrom: '',
              dateTo: parameters?.selectedReports.currencyExposure?.asAtDate,
              dateRangeType: parameters?.selectedReports.currencyExposure?.asAtDateType,
            }}
            setDateRange={handleClickDateForCurrencyExposure}
            inceptionDate={parameters.inceptionDate}
            dateOnlyLabel="As at"
            isDateOnly
          />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container direction="column" spacing={1}>
          {setCurrencyExposurePortfolioParameter && (
            <Grid item>
              <Autocomplete
                noOptionsText="No items to select"
                id="currencyexposure-multiselect"
                fullWidth
                limitTags={1}
                value={
                  clientPortfolios.find((portfolio) => portfolio.investmentProgramId === parameters.selectedReports.currencyExposure.portfolioId) ||
                  null
                }
                options={clientPortfolios}
                onChange={(_e: React.SyntheticEvent<Element, Event>, value: ClientPortfolio | null) => handleSelectPortfolio(value)}
                getOptionLabel={(option: ClientPortfolio) => option.investmentServiceName}
                renderInput={(params) => <TextField {...params} label="Select a Portfolio" margin="normal" fullWidth />}
                isOptionEqualToValue={(option: ClientPortfolio, value: ClientPortfolio) => option.investmentProgramId === value.investmentProgramId}
              />
            </Grid>
          )}
          <Grid item container spacing={2} alignItems="center">
            <Grid item container xs={6} alignItems="center">
              <WO2ToggleSwitch
                onChange={(e) =>
                  setCurrencyExposureHidingOptions({
                    value: e.target.checked,
                    option: HidingOptionCurrencyExposure.ApplyExclusionList,
                  })
                }
                checked={parameters.selectedReports.currencyExposure.applyExclusionList}
                color="primary"
              />
              <Typography variant="h4">Apply Exclusion List</Typography>
            </Grid>
            <Grid item container xs={6} alignItems="center">
              <WO2ToggleSwitch
                onChange={(e) =>
                  setCurrencyExposureHidingOptions({
                    value: e.target.checked,
                    option: HidingOptionCurrencyExposure.IncludeDetailTable,
                  })
                }
                checked={parameters.selectedReports.currencyExposure.includeDetailTable}
                color="primary"
              />
              <Typography variant="h4">Include Detail Table</Typography>
            </Grid>

            {setGroupConsolidatedOption && (
              <Grid item container spacing={4} direction="column">
                <Grid item>
                  <Divider />
                </Grid>
                <Grid item>
                  <ReportGroupingOption
                    value={parameters.selectedReports.currencyExposure.groupOption}
                    setGroupConsolidatedOption={setGroupConsolidatedOption}
                    report={MdaReportTypeField.CurrencyExposure}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
